import { EMPTY_EDITOR_CONTENT } from "@components/ui-kit/TextEditor/TextEditor";
import type { TaskDetailModalStore } from "@components/widgets/Modals/ModalCommunication/store/task-detail-store";
import type { DescriptionMode } from "@components/widgets/Modals/ModalCommunication/store/types";
import { captureEvent } from "@sentry/react";
import {
  CheckTaskRevisionDocument,
  type CheckTaskRevisionQuery,
  type CheckTaskRevisionQueryVariables,
  UpdateTaskDescriptionDocument,
  type UpdateTaskDescriptionMutation,
  type UpdateTaskDescriptionMutationVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { trackEvent } from "@src/services/amplitude";
import type { AppStore } from "@src/stores/AppStore";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { UnionState } from "@src/utils/mobx/states/UnionState";
import { action, computed, makeObservable, observable } from "mobx";

type DescriptionInput = Omit<
  UpdateTaskDescriptionMutationVariables["input"],
  "id"
>;

/** The description editor, its unsaved draft and the revision check. */
export class TaskDescriptionStore {
  mode = new UnionState<DescriptionMode>("read");
  /** The last saved description, a cancel restores it. */
  @observable.ref saved = EMPTY_EDITOR_CONTENT;
  @observable.ref inputValue = "";
  @observable.ref isChanged = false;
  /** Someone else saved the description since it was loaded. */
  needsRevision = new BooleanState();

  private updateMutator = new MutationHelper<
    UpdateTaskDescriptionMutation,
    UpdateTaskDescriptionMutationVariables
  >(UpdateTaskDescriptionDocument);
  private revisionFetcher = new FetchHelper<
    CheckTaskRevisionQuery,
    CheckTaskRevisionQueryVariables
  >(CheckTaskRevisionDocument);

  constructor(
    private readonly appStore: AppStore,
    private readonly detailStore: TaskDetailModalStore,
  ) {
    makeObservable(this);
  }

  @computed get isUpdating(): boolean {
    return this.updateMutator.isLoading.value;
  }

  @computed get canCheckRevision(): boolean {
    return (
      !!this.detailStore.taskId.value &&
      this.detailStore.disclosureState.isOpen &&
      !this.isUpdating &&
      !this.detailStore.actions.editModal.isOpen
    );
  }

  @action.bound
  setInputValue(value: string): void {
    this.inputValue = value;
  }

  @action.bound
  setChanged(changed: boolean): void {
    this.isChanged = changed;
  }

  /** Takes the description of a freshly fetched task. */
  @action.bound
  load(body: string | null | undefined, { keepDraft = false } = {}): void {
    this.saved = body ?? EMPTY_EDITOR_CONTENT;
    this.needsRevision.off();
    // A background refresh must not wipe a description being written
    if (keepDraft && this.isChanged) return;
    this.setInputValue(this.saved);
  }

  @action.bound
  reset(): void {
    this.mode.set("read");
    this.setInputValue(EMPTY_EDITOR_CONTENT);
    this.setChanged(false);
  }

  @action.bound
  async update(input: DescriptionInput): Promise<void> {
    const task = this.detailStore.task.value;
    if (!task) {
      this.appStore.UIStore.toast({ status: "error" });
      captureEvent({
        message: "FE: Missing task ID for updating task description",
      });
      return;
    }

    const [data, error] = await this.updateMutator.mutate({
      input: {
        id: task.id,
        description: input.description,
        addFiles: input.addFiles,
        removeFiles: input.removeFiles,
      },
    });
    if (error) return;

    task.setDescription(data.updateTaskDescription.description);
    task.revision = data.updateTaskDescription.revision;
    this.saved =
      data.updateTaskDescription.description?.body ?? EMPTY_EDITOR_CONTENT;
    this.mode.set("read");
    trackEvent("task", "Updated description");
    this.setInputValue(this.saved);
    // The editor reports one more change after the content is replaced
    setTimeout(() => this.setChanged(false), 150);
  }

  @action.bound
  async checkRevision(): Promise<void> {
    const taskId = this.detailStore.taskId.value;
    if (!this.canCheckRevision || !taskId) return;

    const [data, error] = await this.revisionFetcher.fetch(
      { id: taskId },
      undefined,
      true,
      true,
      "network-only",
    );
    if (error) return;

    const revision = this.detailStore.task.value?.revision;
    if (!data.task.revision || !revision) return;
    if (data.task.revision !== revision) this.needsRevision.on();
    else this.needsRevision.off();
  }
}
